// Hash-based client-side router for the Sparky PWA shell (task 3.5).
//
// Why hash routing: the site is a static bundle behind CloudFront. Hash routes
// (`#/jobs`, `#/quotes/new`) never hit the network or need server rewrites, so
// navigation and deep-linking work even though only `/index.html` is served.
// (CloudFront still maps 403/404 -> /index.html, so a bare path degrades to the
// home screen rather than an error.)
//
// The parsing is pure and unit-tested (router.test.js); createRouter() is the thin
// browser wrapper that listens to `hashchange`.

// The top-level sections the shell can show. `home` is the landing screen; the
// rest each get a full screen (feature content wired up in task 3.6).
export const SECTIONS = Object.freeze(['home', 'jobs', 'quotes', 'invoices', 'clients', 'settings', 'voice']);

// Sections that own individual records and therefore support `/new` and `/<id>`.
const RECORD_SECTIONS = Object.freeze(['jobs', 'quotes', 'invoices', 'clients']);

export const DEFAULT_SECTION = 'home';

function isSection(value) {
  return SECTIONS.includes(value);
}

// Parse a location hash into a route descriptor. Always returns a valid route;
// anything unrecognised falls back to the home section (so a stale or hand-typed
// hash can never leave the app on a blank screen).
//
//   ''              -> { section: 'home',   action: 'list', id: null }
//   '#/jobs'        -> { section: 'jobs',   action: 'list', id: null }
//   '#/jobs/new'    -> { section: 'jobs',   action: 'new',  id: null }
//   '#/quotes/q_42' -> { section: 'quotes', action: 'view', id: 'q_42' }
export function parseRoute(hash) {
  const raw = typeof hash === 'string' ? hash : '';
  // Strip a leading '#', then any leading slashes, then split on '/'.
  const path = raw.replace(/^#/, '').replace(/^\/+/, '');
  const segments = path.split('/').filter(Boolean).map(decodeSegment);

  const section = segments[0];
  if (!section || !isSection(section)) {
    return { section: DEFAULT_SECTION, action: 'list', id: null };
  }

  const second = segments[1];
  if (second && RECORD_SECTIONS.includes(section)) {
    if (second === 'new') return { section, action: 'new', id: null };
    return { section, action: 'view', id: second };
  }
  return { section, action: 'list', id: null };
}

function decodeSegment(seg) {
  try {
    return decodeURIComponent(seg);
  } catch {
    return seg;
  }
}

// Build the canonical hash for a route descriptor — the inverse of parseRoute for
// the shapes the app links to. Used so navigation code never hand-writes hashes.
export function routeToHash({ section = DEFAULT_SECTION, action = 'list', id = null } = {}) {
  const s = isSection(section) ? section : DEFAULT_SECTION;
  if (action === 'new' && RECORD_SECTIONS.includes(s)) return `#/${s}/new`;
  if (action === 'view' && id != null && RECORD_SECTIONS.includes(s)) {
    return `#/${s}/${encodeURIComponent(id)}`;
  }
  return `#/${s}`;
}

// True when two route descriptors point at the same place.
export function sameRoute(a, b) {
  return !!a && !!b && a.section === b.section && a.action === b.action && a.id === b.id;
}

// Wire a callback to hash changes. Returns { start, stop, current, go }:
//   start()  — begin listening and fire onChange once for the current hash
//   stop()   — remove the listener
//   current()— the current parsed route
//   go(route)— navigate (sets location.hash; the hashchange fires onChange)
// The browser globals are only touched inside these methods, so importing this
// module under `node --test` (for parseRoute) never requires a DOM.
export function createRouter(onChange) {
  const handler = () => onChange(parseRoute(window.location.hash));
  return {
    start() {
      window.addEventListener('hashchange', handler);
      handler(); // render the initial route
    },
    stop() {
      window.removeEventListener('hashchange', handler);
    },
    current() {
      return parseRoute(window.location.hash);
    },
    go(route) {
      const hash = typeof route === 'string' ? route : routeToHash(route);
      if (window.location.hash === hash) handler(); // re-render even if unchanged
      else window.location.hash = hash;
    },
  };
}
